import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

type FormData = {
  nume: string;
  email: string;
  description: string;
  telefon: string;
};
type FormField = {
  name: keyof FormData;
  placeholder: string;
  error: string;
  type?: string;
  linesCount?: number;
};

const fields: FormField[] = [
  {
    name: "nume",
    placeholder: "Introduceți numele",
    error: "Numele este obligatoriu.",
  },
  {
    name: "email",
    placeholder: "Introduceți emailul",
    error: "Email-ul este obligatoriu.",
  },
  {
    name: "telefon",
    placeholder: "Introduceți numărul de telefon",
    error: "Numărul de telefon este obligatoriu.",
  },
  {
    name: "description",
    placeholder: "Mesajul dumneavoastră",
    error: "Mesajul este obligatoriu.",
    linesCount: 4,
  },
];

const contactFormSchema = yup.object().shape({
  nume: yup.string().required("Numele este obligatoriu."),
  email: yup
    .string()
    .email("Email invalid")
    .required("Email-ul este obligatoriu."),
  telefon: yup
    .string()
    .matches(/^07\d{8}$/, "Numărul trebuie să conțină doar cifre")
    .required("Numărul de telefon este obligatoriu."),
  description: yup.string().required("Mesajul este obligatoriu."),
});

const ContactForm = () => {
  const navigate = useNavigate();
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(contactFormSchema) });

  const onSubmit = async (data: FormData) => {
    try {
      await axios.post("/api/contact", {
        name: data.nume,
        email: data.email,
        phone: data.telefon,
        message: data.description,
      });
      Swal.fire({
        title: "Mesajul dumneavoastră a fost trimis!",
        customClass: {
          confirmButton: "custom-ok-button", // Apply custom class to the OK button
        },
        text: "Mulțumim că ne-ați contactat! Vom răspunde la întrebarea dumneavoastră în cel mai scurt timp.",
      }).then(() => {
        navigate("/");
      });
      reset();
    } catch {
      toast.error("Ceva nu a mers bine.", {
        duration: 4000,
        position: "top-center",
        style: {
          background: "#f44336",
          color: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        },
      });
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-sm">
      <h4 className="text-2xl font-semibold mb-6">Trimiteți-ne un mesaj</h4>
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {fields.map(({ name, type, placeholder, linesCount }) => (
          <div key={name}>
            {!linesCount ? (
              <input
                {...register(name)}
                type={type ?? "text"}
                placeholder={placeholder}
                className={`w-full p-2 border rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-gray-200 
                                                ${errors[name] ? "border-red-500" : "border-gray-300"}`}
              />
            ) : (
              <textarea
                {...register(name)}
                placeholder={placeholder}
                rows={linesCount}
                className={`w-full p-2 border rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-gray-200 
                                                ${errors[name] ? "border-red-500" : "border-gray-300"}`}
              />
            )}
            {errors[name] && (
              <p className="text-red-500 text-sm mt-1">
                {errors[name]?.message}
              </p>
            )}
          </div>
        ))}
        <button
          type="submit"
          className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700"
        >
          Trimite mesajul
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
